<template>
    <div class="Page__container" :class="additionalClass">
        <MainLayout v-bind="$attrs">
            <template #navigation>
                <RelyingPartySelection
                    v-if="rps.length > 1"
                    :options="rps"
                    :value="currentRp"
                    @input="changeRp"
                />
                <UserBox>
                    <MenuLink label="Product updates" @click="openProductUpdates" />
                    <MenuLink label="Customer Support" @click="openSite('support')" />
                    <MenuLink v-if="currentRp" label="Help Articles" @click="openHelpArticles" />
                </UserBox>
            </template>

            <template #sidebar>
                <SidebarMenu :show-powered-by="shouldDisplayPoweredBy">
                    <template #logo>
                        <img alt="Evident" :src="logoUrl">
                    </template>
                </SidebarMenu>
            </template>
            <slot />
        </MainLayout>
    </div>
</template>

<style lang="scss">
.Page {
    &__container {
        display: grid;
    }
}

.PoweredByEvidentLogo {
    position: fixed;
    bottom: 10px;
    left: 35px;
}
</style>

<script lang="ts">

    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faComment } from '@fortawesome/free-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { MenuLink } from '@evidentid/dashboard-commons/components/Menu';
    import UserBox from '@evidentid/dashboard-commons/modules/auth/components/UserBox.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { PartnersSettingsService } from '@evidentid/iam-client/services/partners-settings-service/partnersSettingsService';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import logoUrl from '@/assets/icons/logo.svg';
    import powerByEvidentLogo from '@/assets/images/powered_by_evident.svg';
    import NewButton from '@/components/new-button/NewButton.vue';
    import RelyingPartySelection from '@/components/replying-party-selection/RelyingPartySelection.vue';
    import SidebarMenu from '@/components/sidebar-menu/SidebarMenu.vue';
    import { MainLayout } from '@/layouts';
    import { useLocalStorageStore } from '@/modules/local-storage/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';
    import { useProductUpdatesStore } from '@/modules/product-updates/store';

    @Component({
        components: {
            UserBox,
            RelyingPartySelection,
            SidebarMenu,
            MenuLink,
            MainLayout,
        },
    })
    export default class Page extends Vue {
        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private RbacFeature = RbacFeature;
        private logoUrl = logoUrl;
        private powerByEvidentLogo = powerByEvidentLogo;
        private shouldDisplayPoweredBy = false;

        private mounted() {
            useLocalStorageStore().loadUserPreference({ email: this.userEmail });

            PartnersSettingsService.fetchPartnerSettings()
                .then((response) => {
                    if (response) {
                        this.shouldDisplayPoweredBy = true;
                    }
                    if (response?.wordmark_url) {
                        this.logoUrl = response.wordmark_url;
                    }
                });
        }

        private get rps() {
            return this.$rp.available;
        }

        private get currentRp() {
            return this.$rp.current;
        }

        private get userEmail(): string {
            return useUserStore().data?.email || '';
        }

        private get additionalClass(): string {
            return (this.$route.meta?.pageClass as string | undefined) ?? '';
        }

        private get loading(): boolean {
            return this.pageLayoutStore.loading;
        }

        private changeRp(rpId: string) {
            this.$rp.change(rpId);
        }

        private openSite(name: 'support'): void {
            const urls = {
                support: 'https://www.evidentid.com/support/insurance/',
            };
            if (urls[name]) {
                window.open(urls[name], '_blank');
            }
        }

        private openHelpArticles(): void {
            if (!this.currentRp) {
                return;
            }
            this.$router.push({ name: 'kbHelp', params: { rpId: this.currentRp } });
        }

        private openProductUpdates(): void {
            const productUpdatesStore = useProductUpdatesStore();
            productUpdatesStore.openProductUpdatesArchive();
            productUpdatesStore.loadProductUpdatesArchive({ force: true });
        }
    }
</script>
